/**
 * /ja/prd — PRD (製品要求仕様書) concept page (JA). Closes the concept hub.
 * PRD-drift reframe. Links /ja/product-roadmap + /ja/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function JaPrd() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/prd"
      hreflangLocales={['ja']}
      documentTitle="PRDとは？製品要求仕様書の書き方とテンプレート · Draftlize"
      documentDescription="PRD（製品要求仕様書）の意味・構成・テンプレートを解説。スプリントごとに陳腐化しない、「生きたPRD」の作り方まで踏み込みます。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            PRD（製品要求仕様書）<br />
            <em>とは。</em>
          </h1>
          <p className="lede">
            <strong>PRD</strong>（Product Requirements Document／製品要求仕様書）は、<em>何を</em>作るか、そして<em>なぜ</em>かを定める文書です。課題、スコープ、要求、その裏の決定をまとめ、チームと関係者が同じ絵を持てるようにします。弱点はその後にあります——2〜3スプリントで PRD は現実とずれ、<em>陳腐化（ドリフト）</em>する。ここでは構成とテンプレート、そしてドリフトを防ぐ方法を解説します。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">崩れない PRD の構成。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>課題と目標</strong>——誰の課題か、なぜ今か、どんな成果を狙うか。</li>
            <li><strong>スコープ（対象／対象外）</strong>——あえて含めないもの。</li>
            <li><strong>要求</strong>——機能・非機能、優先度つき。</li>
            <li><strong>決定とトレードオフ</strong>——何を検討し、なぜ捨てたか。</li>
            <li><strong>未解決の問い・成功基準</strong>。</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            このテンプレートはそのまま使えます。最も価値があり、最も早く古くなるのが4番——決定です。PRD は <a href="/ja/product-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>ロードマップ</a> とその下の決定の、見える先端にすぎません。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">本当の問題は <em>PRD ドリフト。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            PRD は承認された日には正しい。その後、会議が前提を変え、エッジケースが決定を覆す——なのに文書は止まったまま。12ページの文書を手で追従させる人はいません。数週間後、AI エージェントはもう成立しない仕様の上に作り始めます。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">要求はカードになる</h3>
            <p>要求と決定が、7ページ目の一行ではなく参照できるカードに。理由と担当者を持ち、ID で辿れます。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">決定が変わると PRD が知らせる</h3>
            <p>後の決定が前を覆すと、依存カード——つまり該当する PRD 部分——が自動で stale に。文書が3週間も黙って嘘をつきません。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI エージェントが最新の仕様を読む</h3>
            <p>Claude Code や Cursor が MCP 経由で PRD を読みます——最新か、明示的に stale か。状態の分からない静的文書ではありません。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          PRD は承認日には正しい。問題は、その後の6週間、誰も追従させないことだ。
        </blockquote>
        <cite>要求をカードにすれば、PRD 自身が「古くなった」と知らせる。それが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PRD と仕様書の違いは？', a: 'PRD は「何を・なぜ」を定める上位の文書、仕様書は「どう作るか」に踏み込みます。PRD の決定が変われば仕様も変わるべきで、その連動が肝心です。' },
            { q: '誰が書く？', a: '多くは PM が、エンジニアやデザインと協働して書きます。大事なのは、中の決定が後から辿れて、理由が残ることです。' },
            { q: 'どのくらいの分量？', a: '課題・スコープ・要求・決定が伝われば十分です。長さより、覆されたときに気づける仕組みが重要です。' },
            { q: '陳腐化を防ぐには？', a: '要求と決定を、固定した段落ではなくリンクしたカードにします。決定が変わると Draftlize が依存箇所を自動で stale 表示します。詳しくは <link>。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>
                {f.a.replace('詳しくは <link>。','')}
                {i === 3 && (<>{'詳しくは '}<a href="/ja/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>意思決定ログ</a>{'。'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">ドリフトしない PRD を<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            PRD をリンクしたカードで書けば、各要求が参照可能に。決定が変われば該当箇所を自動で stale——PRD ドリフトはもう終わりです。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
